import { IconButton } from '@mui/material';
import { VyIcon } from '@vy/ui-design-system';
import type { PerUnitProductVariant } from '@vy/services';
import { isMultiple, isSingleItem, removeInIndex } from '@vy/utils';
import cn from 'classnames';
import React, { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import type { PlanInputForm } from '../../../../CreatePlanForm/create-plan-form-types/plan-input-form';
import { PerUnitUsageChargeEditVariantsListItem } from './PerUnitUsageChargeEditVariantsListItem';

type PerUnitUsageChargeEditVariantsListProps = {
  variants: PerUnitProductVariant[];
  productIndex: number;
};

export const PerUnitUsageChargeEditVariantsList: React.FC<PerUnitUsageChargeEditVariantsListProps> = ({
  variants,
  productIndex,
}) => {
  const { getValues, setValue } = useFormContext<PlanInputForm>();
  const productVariants = getValues(`usageProducts.${productIndex}.variants`);

  const onDelete = useCallback(
    (productVariantIndex: number) => {
      const updatedProductVariants = removeInIndex(productVariants, productVariantIndex);
      setValue(`usageProducts.${productIndex}.variants`, updatedProductVariants);

      // Dropping back to a single variant un-groups the product, so clear the
      // group name — otherwise it lingers and is shown on invoices (ENG-6069).
      if (isSingleItem(updatedProductVariants)) {
        setValue(`usageProducts.${productIndex}.name`, undefined);
      }
    },
    [productIndex, productVariants],
  );

  return (
    <div className="grid grid-cols-1 gap-5">
      {variants.map((variant, variantIndex) => (
        <div
          key={variant.id}
          className={cn(
            'even:bg-product-bk flex flex-row-reverse justify-between',
            variantIndex === 0 ? 'px-6' : 'pl-6',
          )}
        >
          {isMultiple(variants) && variantIndex != 0 && (
            <div className="self-center mt-13 ml-3 mr-6">
              <IconButton onClick={() => onDelete(variantIndex)}>
                <VyIcon icon="Delete" />
              </IconButton>
            </div>
          )}
          <div className="grow">
            <PerUnitUsageChargeEditVariantsListItem
              key={variant.id}
              variant={variant}
              productIndex={productIndex}
              variantIndex={variantIndex}
            />
          </div>
        </div>
      ))}
    </div>
  );
};
